import { FC, MouseEvent, useEffect, useRef, useState } from 'react';
import { Box, Tooltip } from '@mui/material';

import IconButton from '@/components/ui/IconButton';

interface KopierKnopfProps {
  /** Was in die Zwischenablage geht. */
  wert: string;
  /** Für Tooltip und Screenreader: „E-Mail" → „E-Mail kopieren". */
  bezeichnung: string;
}

/**
 * Kopiert einen Wert in die Zwischenablage — 1,5 s Häkchen als Rückmeldung.
 * Muster wie die Copy-Knöpfe in `MeetingDetailView`/`OeffentlicheBuchungAbschnitt`.
 *
 * Der Klick geht NICHT an die umgebende Fläche weiter: in den Grunddaten liegt
 * der Knopf im klickbaren Wert, und ein Klick dort öffnet das Bearbeiten — genau
 * der Weg, auf dem beim Kopieren Telefonnummern überschrieben wurden.
 */
const KopierKnopf: FC<KopierKnopfProps> = ({ wert, bezeichnung }) => {
  const [kopiert, setKopiert] = useState(false);
  const timer = useRef<number>();

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const kopiere = async (e: MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(wert);
      setKopiert(true);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setKopiert(false), 1500);
    } catch {
      // Ohne Zwischenablage-Recht bleibt der Wert sichtbar und markierbar.
    }
  };

  return (
    <Tooltip title={kopiert ? 'Kopiert' : `${bezeichnung} kopieren`}>
      <Box component="span" onClick={kopiere} sx={{ display: 'inline-flex', flexShrink: 0 }}>
        <IconButton
          icon={kopiert ? 'CheckIcon' : 'Copy'}
          size={14}
          ariaLabel={`${bezeichnung} kopieren`}
        />
      </Box>
    </Tooltip>
  );
};

export default KopierKnopf;
